
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export class BucketService {
  /**
   * Ensures required buckets exist
   */
  static async ensureRequiredBuckets(): Promise<void> {
    try {
      console.log('Ensuring required buckets exist...');
      
      // List all available buckets first
      const { data: buckets, error: listError } = await supabase.storage.listBuckets();
      
      if (listError) {
        console.error('Error listing buckets:', listError);
        return;
      }
      
      const bucketNames = buckets?.map(b => b.name) || [];
      console.log('Available buckets:', bucketNames);
      
      // Define required buckets
      const requiredBuckets = [
        { name: 'public-images', isPublic: true },
        { name: 'company-assets', isPublic: true },
        { name: 'documents', isPublic: true },
        { name: 'business-plans', isPublic: true }
      ];
      
      // Create buckets that don't exist
      for (const bucket of requiredBuckets) {
        if (!bucketNames.includes(bucket.name)) {
          console.log(`Creating bucket: ${bucket.name}`);
          try {
            const { error } = await supabase.storage.createBucket(bucket.name, {
              public: bucket.isPublic
            });
            
            if (error) {
              console.error(`Error creating bucket ${bucket.name}:`, error);
              // If we can't create the bucket, try to use an existing one
              console.log(`Will try to use 'public-images' as fallback for uploads`);
            } else {
              console.log(`Successfully created bucket: ${bucket.name}`);
            }
          } catch (bucketError) {
            console.error(`Exception creating bucket ${bucket.name}:`, bucketError);
          }
        } else {
          console.log(`Bucket ${bucket.name} already exists`);
        }
      }
      
      console.log('Bucket setup completed');
    } catch (error) {
      console.error('Exception ensuring required buckets:', error);
    }
  }

  /**
   * Checks if a bucket exists
   */
  static async checkBucketExists(bucketName: string): Promise<boolean> {
    try {
      const { data: buckets, error } = await supabase.storage.listBuckets();
      
      if (error) {
        console.error('Error checking if bucket exists:', error);
        return false;
      }
      
      return buckets?.some(b => b.name === bucketName) || false;
    } catch (error) {
      console.error('Exception checking if bucket exists:', error);
      return false;
    }
  }

  /**
   * Creates a new bucket - Note: This requires admin privileges
   * In most cases, buckets should be created via migrations
   */
  static async createBucket(bucketName: string, isPublic: boolean = true): Promise<boolean> {
    try {
      // First check if bucket already exists
      const exists = await this.checkBucketExists(bucketName);
      
      if (exists) {
        console.log(`Bucket ${bucketName} already exists`);
        return true;
      }
      
      console.warn('Creating buckets requires admin privileges and may fail due to RLS policies');
      
      const { error } = await supabase.storage.createBucket(bucketName, {
        public: isPublic
      });
      
      if (error) {
        console.error('Error creating bucket:', error);
        
        // Provide a more helpful error message
        if (error.message.includes('row-level security policy')) {
          toast.error('Failed to create bucket: Insufficient permissions. Buckets should be created through migrations.');
        } else {
          toast.error(`Failed to create bucket: ${error.message}`);
        }
        
        return false;
      }
      
      toast.success(`Bucket "${bucketName}" created successfully`);
      return true;
    } catch (error) {
      console.error('Exception creating bucket:', error);
      toast.error('Failed to create bucket due to an unexpected error');
      return false;
    }
  }
}
